/**
 * V38.40 — Panneau Super Admin « Pied de page front-office ».
 *
 * Gère SANS CODE les colonnes du footer public (Navigation, FTPE Outremer,
 * Légal, …) et leurs liens : CRUD, duplication, drag-and-drop, sélection
 * multiple + actions de masse (Afficher / Masquer / Supprimer), audiences et
 * planification.
 *
 * Le footer public (`Footer.js`) lit la config via /api/public/frontoffice-footer.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Eye, EyeOff, Pencil, Trash2, Plus, Save, PanelBottom, Loader2, ExternalLink, Clock, Lock,
  GripVertical, ChevronDown, ChevronRight, X, Copy,
  Sparkles, Landmark, Mail, Zap, Shield, Users, Building2, GraduationCap, Globe, Heart, FileText, Phone, MapPin, Target,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import InstitutionalHero from './ui/InstitutionalHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = ['Sparkles', 'Landmark', 'Mail', 'Zap', 'Shield', 'Users', 'Building2', 'GraduationCap', 'Globe', 'Heart', 'FileText', 'Phone', 'MapPin', 'Target'];
const AUDIENCES = [
  { value: 'all', label: 'Tout le monde' },
  { value: 'guests', label: 'Visiteurs (non connectés)' },
  { value: 'authenticated', label: 'Connectés (tous rôles)' },
  { value: 'roles', label: 'Rôles précis…' },
];
const ROLES = ['public', 'prospect', 'member', 'beneficiaire_pro', 'beneficiaire_particulier', 'cooper', 'expert', 'admin', 'super_admin'];

const ICON_MAP = { Sparkles, Landmark, Mail, Zap, Shield, Users, Building2, GraduationCap, Globe, Heart, FileText, Phone, MapPin, Target };

const getAuthHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const toLocalInput = (v) => (v ? String(v).slice(0, 16) : '');

const IconChip = ({ icon }) => {
  const Ico = ICON_MAP[icon] || Sparkles;
  return (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-slate-800">
      <Ico className="w-4 h-4 text-amber-400" />
    </div>
  );
};

function VisBadges({ node }) {
  const aud = node.audience || 'all';
  const scheduled = !!(node.visible_from || node.visible_until);
  const audLabel = aud === 'roles' ? ((node.roles || []).join(', ') || 'rôles')
    : aud === 'guests' ? 'visiteurs' : aud === 'authenticated' ? 'connectés' : '';
  return (
    <>
      {aud !== 'all' && (
        <Badge variant="outline" className="text-[9px] gap-0.5 border-indigo-300 text-indigo-700">
          <Lock className="h-2.5 w-2.5" />{audLabel}
        </Badge>
      )}
      {scheduled && (
        <Badge variant="outline" className="text-[9px] gap-0.5 border-amber-300 text-amber-700">
          <Clock className="h-2.5 w-2.5" />planifié
        </Badge>
      )}
    </>
  );
}

function VisibilityFields({ form, setForm }) {
  const toggleRole = (r) => {
    const cur = form.roles || [];
    setForm({ ...form, roles: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] });
  };
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Visibilité avancée</p>
      <div>
        <Label className="text-xs">Audience</Label>
        <select value={form.audience || 'all'} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="footer-vis-audience">
          {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      {form.audience === 'roles' && (
        <div className="flex flex-wrap gap-1.5" data-testid="footer-vis-roles">
          {ROLES.map((r) => (
            <button key={r} type="button" onClick={() => toggleRole(r)}
              className={`text-[11px] px-2 py-1 rounded-full border ${(form.roles || []).includes(r) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}
              data-testid={`footer-vis-role-${r}`}>
              {r}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Visible à partir du</Label>
          <Input type="datetime-local" value={toLocalInput(form.visible_from)} onChange={(e) => setForm({ ...form, visible_from: e.target.value })} className="text-sm" data-testid="footer-vis-from" />
        </div>
        <div>
          <Label className="text-xs">Jusqu'au</Label>
          <Input type="datetime-local" value={toLocalInput(form.visible_until)} onChange={(e) => setForm({ ...form, visible_until: e.target.value })} className="text-sm" data-testid="footer-vis-until" />
        </div>
      </div>
      <p className="text-[10px] text-slate-400">Laissez les dates vides pour une visibilité permanente. Fuseau : heure du serveur (UTC).</p>
    </div>
  );
}

const EMPTY_LINK = { label: '', to: '', external: false, visible: true, audience: 'all', roles: [], visible_from: '', visible_until: '' };
const EMPTY_COLUMN = { label: '', icon: 'Sparkles', visible: true, audience: 'all', roles: [], visible_from: '', visible_until: '' };

// ---- Ligne de lien (sortable) ---------------------------------------------
function SortableLinkRow({ col, it, selected, onToggleSelect, onToggleVisible, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center justify-between gap-2 px-2 py-2 rounded-lg border ${selected ? 'border-indigo-300 bg-indigo-50/50' : 'border-transparent hover:bg-slate-50'} ${it.visible === false ? 'opacity-50' : ''}`}
      data-testid={`footer-link-row-${it.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(col.id, it.id)} data-testid={`footer-link-select-${it.id}`} />
        <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none text-slate-300 hover:text-slate-600" data-testid={`footer-link-drag-${it.id}`} title="Glisser pour réordonner">
          <GripVertical className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
            {it.label}
            {it.external && <Badge variant="outline" className="text-[8px]">externe</Badge>}
          </div>
          <p className="text-[11px] text-slate-400 font-mono truncate flex items-center gap-1"><ExternalLink className="h-2.5 w-2.5" />{it.to || '—'}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="hidden md:flex items-center gap-1 mr-1"><VisBadges node={it} /></span>
        <button onClick={() => onToggleVisible(col.id, it)} className="p-1.5 rounded hover:bg-slate-200" data-testid={`footer-link-toggle-${it.id}`} title={it.visible !== false ? 'Masquer' : 'Afficher'}>
          {it.visible !== false ? <Eye className="h-3.5 w-3.5 text-emerald-600" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
        </button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(col.id, it)} data-testid={`footer-link-edit-${it.id}`}><Pencil className="h-3 w-3" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => onDelete(col.id, it)} data-testid={`footer-link-delete-${it.id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}

// ---- Carte de colonne (sortable, repliable) -------------------------------
function SortableColumnCard({
  col, collapsed, onToggleCollapse, selected, onToggleSelect, selectedItems, onToggleSelectItem,
  onEditColumn, onDeleteColumn, onDuplicateColumn, onToggleColumnVisible, onAddLink, onLinksDragEnd,
  onToggleLinkVisible, onEditLink, onDeleteLink, sensors,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1, zIndex: isDragging ? 30 : undefined };
  const items = col.items || [];
  const visibleCount = items.filter((i) => i.visible !== false).length;
  return (
    <Card ref={setNodeRef} style={style} className={`overflow-hidden ${selected ? 'ring-2 ring-indigo-400' : ''} ${col.visible ? '' : 'opacity-60'}`} data-testid={`footer-col-card-${col.key}`}>
      <div className="flex items-center justify-between px-3 py-3 bg-gradient-to-r from-slate-50 to-white border-b">
        <div className="flex items-center gap-2.5 min-w-0">
          <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(col.id)} data-testid={`footer-col-select-${col.key}`} />
          <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none text-slate-300 hover:text-slate-600" data-testid={`footer-col-drag-${col.key}`} title="Glisser pour réordonner">
            <GripVertical className="h-5 w-5" />
          </span>
          <button onClick={() => onToggleCollapse(col.id)} className="text-slate-400 hover:text-slate-700" data-testid={`footer-col-collapse-${col.key}`}>
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <IconChip icon={col.icon} />
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 flex-wrap">
              {col.label}
              {col.is_seed && <Badge variant="outline" className="text-[9px]">seed</Badge>}
              <VisBadges node={col} />
            </h3>
            <p className="text-[11px] text-slate-400">{items.length} lien(s) · {visibleCount} visible(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onToggleColumnVisible(col)} className="p-2 rounded hover:bg-slate-200" title={col.visible ? 'Masquer' : 'Afficher'} data-testid={`footer-col-toggle-${col.key}`}>
            {col.visible ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
          </button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onDuplicateColumn(col)} title="Dupliquer" data-testid={`footer-col-duplicate-${col.key}`}><Copy className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEditColumn(col)} data-testid={`footer-col-edit-${col.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => onDeleteColumn(col)} data-testid={`footer-col-delete-${col.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      {!collapsed && (
        <CardContent className="p-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onLinksDragEnd(col, e)}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              {items.map((it) => (
                <SortableLinkRow key={it.id} col={col} it={it}
                  selected={selectedItems.has(`${col.id}::${it.id}`)}
                  onToggleSelect={onToggleSelectItem}
                  onToggleVisible={onToggleLinkVisible} onEdit={onEditLink} onDelete={onDeleteLink} />
              ))}
            </SortableContext>
          </DndContext>
          <Button variant="outline" size="sm" className="mt-2 w-full border-dashed" onClick={() => onAddLink(col.id)} data-testid={`footer-add-link-${col.key}`}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Ajouter un lien
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

export default function FrontOfficeFooterPanel() {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedCols, setSelectedCols] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());

  const [colDialog, setColDialog] = useState(false);
  const [editingCol, setEditingCol] = useState(null);
  const [colForm, setColForm] = useState(EMPTY_COLUMN);

  const [linkDialog, setLinkDialog] = useState(false);
  const [linkColId, setLinkColId] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm, setLinkForm] = useState(EMPTY_LINK);

  const submitLock = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchColumns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/frontoffice-footer`, { headers: getAuthHeader() });
      setColumns(res.data?.columns || []);
    } catch {
      toast.error('Impossible de charger le pied de page');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchColumns(); }, [fetchColumns]);

  // ---- Sélection ----
  const toggleSelectCol = (id) => setSelectedCols((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectItem = (colId, itemId) => setSelectedItems((p) => { const k = `${colId}::${itemId}`; const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const clearSelection = () => { setSelectedCols(new Set()); setSelectedItems(new Set()); };
  const selectionCount = selectedCols.size + selectedItems.size;

  const toggleCollapse = (id) => setCollapsed((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allCollapsed = columns.length > 0 && columns.every((c) => collapsed.has(c.id));
  const toggleCollapseAll = () => setCollapsed(allCollapsed ? new Set() : new Set(columns.map((c) => c.id)));

  // ---- Drag & drop ----
  const onColumnsDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = columns.findIndex((c) => c.id === active.id);
    const newIdx = columns.findIndex((c) => c.id === over.id);
    const next = arrayMove(columns, oldIdx, newIdx);
    setColumns(next);
    try {
      await axios.post(`${API}/admin/frontoffice-footer/reorder`, { order: next.map((c) => c.id) }, { headers: getAuthHeader() });
    } catch { toast.error('Échec du réordonnancement des colonnes'); fetchColumns(); }
  };

  const onLinksDragEnd = async (col, { active, over }) => {
    if (!over || active.id === over.id) return;
    const items = col.items || [];
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const nextItems = arrayMove(items, oldIdx, newIdx);
    setColumns((prev) => prev.map((c) => (c.id === col.id ? { ...c, items: nextItems } : c)));
    try {
      await axios.post(`${API}/admin/frontoffice-footer/${col.id}/items/reorder`, { order: nextItems.map((i) => i.id) }, { headers: getAuthHeader() });
    } catch { toast.error('Échec du réordonnancement des liens'); fetchColumns(); }
  };

  // ---- Actions de masse ----
  const applyBulk = async (action) => {
    const colIds = [...selectedCols];
    const items = [...selectedItems].map((s) => { const [cId, iId] = s.split('::'); return { cId, iId }; });
    if (action === 'delete' && !window.confirm(`Supprimer définitivement ${selectionCount} élément(s) sélectionné(s) ?`)) return;
    setSaving(true);
    try {
      const calls = [];
      for (const id of colIds) {
        calls.push(action === 'delete'
          ? axios.delete(`${API}/admin/frontoffice-footer/${id}`, { headers: getAuthHeader() })
          : axios.put(`${API}/admin/frontoffice-footer/${id}`, { visible: action === 'show' }, { headers: getAuthHeader() }));
      }
      for (const { cId, iId } of items) {
        calls.push(action === 'delete'
          ? axios.delete(`${API}/admin/frontoffice-footer/${cId}/items/${iId}`, { headers: getAuthHeader() })
          : axios.put(`${API}/admin/frontoffice-footer/${cId}/items/${iId}`, { visible: action === 'show' }, { headers: getAuthHeader() }));
      }
      await Promise.all(calls);
      toast.success(`Action appliquée à ${selectionCount} élément(s)`);
      clearSelection();
      fetchColumns();
    } catch {
      toast.error('Échec (partiel) de l\u2019action de masse');
      fetchColumns();
    } finally { setSaving(false); }
  };

  // ---- Colonne CRUD ----
  const openCreateCol = () => { setEditingCol(null); setColForm(EMPTY_COLUMN); setColDialog(true); };
  const openEditCol = (c) => { setEditingCol(c); setColForm({ label: c.label, icon: c.icon, visible: c.visible, audience: c.audience || 'all', roles: c.roles || [], visible_from: c.visible_from || '', visible_until: c.visible_until || '' }); setColDialog(true); };

  const saveCol = async () => {
    if (!colForm.label.trim()) { toast.error('Libellé de la colonne requis'); return; }
    if (submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    try {
      if (editingCol) {
        await axios.put(`${API}/admin/frontoffice-footer/${editingCol.id}`, colForm, { headers: getAuthHeader() });
        toast.success('Colonne mise à jour');
      } else {
        await axios.post(`${API}/admin/frontoffice-footer`, colForm, { headers: getAuthHeader() });
        toast.success('Colonne créée');
      }
      setColDialog(false);
      fetchColumns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\u2019enregistrement');
    } finally { setSaving(false); submitLock.current = false; }
  };

  const deleteCol = async (c) => {
    if (!window.confirm(`Supprimer la colonne « ${c.label} » et tous ses liens ?`)) return;
    try {
      await axios.delete(`${API}/admin/frontoffice-footer/${c.id}`, { headers: getAuthHeader() });
      toast.success('Colonne supprimée');
      fetchColumns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de la suppression'); }
  };

  const duplicateCol = async (c) => {
    try {
      await axios.post(`${API}/admin/frontoffice-footer/${c.id}/duplicate`, {}, { headers: getAuthHeader() });
      toast.success('Colonne dupliquée');
      fetchColumns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de la duplication'); }
  };

  const toggleColVisible = async (c) => {
    try {
      await axios.put(`${API}/admin/frontoffice-footer/${c.id}`, { visible: !c.visible }, { headers: getAuthHeader() });
      fetchColumns();
    } catch { toast.error('Échec'); }
  };

  // ---- Lien CRUD ----
  const openCreateLink = (colId) => { setLinkColId(colId); setEditingLink(null); setLinkForm(EMPTY_LINK); setLinkDialog(true); };
  const openEditLink = (colId, it) => {
    setLinkColId(colId); setEditingLink(it);
    setLinkForm({ label: it.label, to: it.to || '', external: !!it.external, visible: it.visible !== false, audience: it.audience || 'all', roles: it.roles || [], visible_from: it.visible_from || '', visible_until: it.visible_until || '' });
    setLinkDialog(true);
  };

  const saveLink = async () => {
    if (!linkForm.label.trim()) { toast.error('Libellé du lien requis'); return; }
    if (submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    try {
      if (editingLink) {
        await axios.put(`${API}/admin/frontoffice-footer/${linkColId}/items/${editingLink.id}`, linkForm, { headers: getAuthHeader() });
        toast.success('Lien mis à jour');
      } else {
        await axios.post(`${API}/admin/frontoffice-footer/${linkColId}/items`, linkForm, { headers: getAuthHeader() });
        toast.success('Lien ajouté');
      }
      setLinkDialog(false);
      fetchColumns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\u2019enregistrement');
    } finally { setSaving(false); submitLock.current = false; }
  };

  const deleteLink = async (colId, it) => {
    if (!window.confirm(`Supprimer le lien « ${it.label} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/frontoffice-footer/${colId}/items/${it.id}`, { headers: getAuthHeader() });
      toast.success('Lien supprimé');
      fetchColumns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec'); }
  };

  const toggleLinkVisible = async (colId, it) => {
    try {
      await axios.put(`${API}/admin/frontoffice-footer/${colId}/items/${it.id}`, { visible: !(it.visible !== false) }, { headers: getAuthHeader() });
      fetchColumns();
    } catch { toast.error('Échec'); }
  };

  return (
    <div className="space-y-6" data-testid="frontoffice-footer-panel">
      <InstitutionalHero
        gradient="bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900"
        badge="Front-office"
        title="Pied de page public"
        subtitle="Gérez les colonnes et liens du footer — glissez-déposez pour réordonner, dupliquez, sélectionnez pour des actions de masse. Audiences et planification incluses, sans code."
        icon={PanelBottom}
        actions={
          <Button onClick={openCreateCol} className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-semibold" data-testid="footer-create-col-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvelle colonne
          </Button>
        }
        kpis={[
          { label: 'Colonnes', value: columns.length },
          { label: 'Visibles', value: columns.filter((c) => c.visible).length },
          { label: 'Liens', value: columns.reduce((s, c) => s + (c.items?.length || 0), 0) },
        ]}
      />

      {!loading && columns.length > 0 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={toggleCollapseAll} data-testid="footer-collapse-all-btn">
            {allCollapsed ? <ChevronDown className="h-4 w-4 mr-1.5" /> : <ChevronRight className="h-4 w-4 mr-1.5" />}
            {allCollapsed ? 'Tout déplier' : 'Tout replier'}
          </Button>
          <p className="text-xs text-slate-400">Astuce : glissez la poignée <GripVertical className="h-3 w-3 inline" /> pour réordonner</p>
        </div>
      )}

      {selectionCount > 0 && (
        <div className="sticky top-2 z-40 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-600 px-4 py-3 shadow-lg" data-testid="footer-bulk-bar">
          <span className="text-sm font-semibold text-white">{selectionCount} sélectionné(s)</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => applyBulk('show')} disabled={saving} data-testid="footer-bulk-show"><Eye className="h-4 w-4 mr-1.5" />Afficher</Button>
            <Button size="sm" variant="secondary" onClick={() => applyBulk('hide')} disabled={saving} data-testid="footer-bulk-hide"><EyeOff className="h-4 w-4 mr-1.5" />Masquer</Button>
            <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => applyBulk('delete')} disabled={saving} data-testid="footer-bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" />Supprimer</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-indigo-700" onClick={clearSelection} data-testid="footer-bulk-clear"><X className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onColumnsDragEnd}>
          <SortableContext items={columns.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {columns.map((c) => (
                <SortableColumnCard
                  key={c.id} col={c}
                  collapsed={collapsed.has(c.id)} onToggleCollapse={toggleCollapse}
                  selected={selectedCols.has(c.id)} onToggleSelect={toggleSelectCol}
                  selectedItems={selectedItems} onToggleSelectItem={toggleSelectItem}
                  onEditColumn={openEditCol} onDeleteColumn={deleteCol} onDuplicateColumn={duplicateCol} onToggleColumnVisible={toggleColVisible}
                  onAddLink={openCreateLink} onLinksDragEnd={onLinksDragEnd}
                  onToggleLinkVisible={toggleLinkVisible} onEditLink={openEditLink} onDeleteLink={deleteLink}
                  sensors={sensors}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Dialog Colonne */}
      <Dialog open={colDialog} onOpenChange={setColDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="footer-col-form-modal">
          <DialogHeader><DialogTitle>{editingCol ? 'Modifier la colonne' : 'Nouvelle colonne'}</DialogTitle><DialogDescription>Colonne affichée dans le pied de page public.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Libellé *</Label>
              <Input value={colForm.label} onChange={(e) => setColForm({ ...colForm, label: e.target.value })} placeholder="ex : Navigation" data-testid="footer-col-form-label" />
            </div>
            <div>
              <Label className="text-xs">Icône</Label>
              <select value={colForm.icon} onChange={(e) => setColForm({ ...colForm, icon: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="footer-col-form-icon">
                {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={colForm.visible} onCheckedChange={(v) => setColForm({ ...colForm, visible: v })} data-testid="footer-col-form-visible" />
              <Label className="text-xs">Visible dans le footer public</Label>
            </div>
            <VisibilityFields form={colForm} setForm={setColForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColDialog(false)}>Annuler</Button>
            <Button onClick={saveCol} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="footer-col-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-1.5" />{editingCol ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Lien */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="footer-link-form-modal">
          <DialogHeader><DialogTitle>{editingLink ? 'Modifier le lien' : 'Nouveau lien'}</DialogTitle><DialogDescription>Lien affiché dans la colonne du pied de page.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Libellé *</Label>
              <Input value={linkForm.label} onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} placeholder="ex : Mentions légales" data-testid="footer-link-form-label" />
            </div>
            <div>
              <Label className="text-xs">Lien / route de destination</Label>
              <Input value={linkForm.to} onChange={(e) => setLinkForm({ ...linkForm, to: e.target.value })} placeholder="ex : /mentions-legales ou https://…" className="font-mono text-sm" data-testid="footer-link-form-to" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={linkForm.external} onCheckedChange={(v) => setLinkForm({ ...linkForm, external: v })} data-testid="footer-link-form-external" />
              <Label className="text-xs">Lien externe (nouvel onglet)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={linkForm.visible} onCheckedChange={(v) => setLinkForm({ ...linkForm, visible: v })} data-testid="footer-link-form-visible" />
              <Label className="text-xs">Visible</Label>
            </div>
            <VisibilityFields form={linkForm} setForm={setLinkForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(false)}>Annuler</Button>
            <Button onClick={saveLink} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="footer-link-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-1.5" />{editingLink ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
